<script setup lang="ts">
import { computed, onMounted, Ref, ref, watch } from "vue";
import DimensionSlider from "./DimensionSlider.vue";
import { LanguageManagementAPI } from "../../../api";
import { Identifier, Text } from "../../../types/PersologOnlineTypes";
import { patternOrder } from "../../../utils/GeneralUtils.ts";
import { getPatternsForDiagramm } from "../../../utils/JobMatchUtils.ts";
import { i18n } from "../../../main.ts";

const emit = defineEmits(["update:range-values"]);

const props = defineProps({
    editable: {
        type: Boolean,
        default: true,
    },
    dominantRange: {
        type: Array<number>,
        default: () => [12.5, 87.5],
    },
    initiativeRange: {
        type: Array<number>,
        default: () => [12.5, 87.5],
    },
    steadyRange: {
        type: Array<number>,
        default: () => [12.5, 87.5],
    },
    cautiousRange: {
        type: Array<number>,
        default: () => [12.5, 87.5],
    },
    language: {
        type: String,
        required: true,
    },
});

/*
 * The value of the range slider for each slider
 */
const dominantRange = ref(props.dominantRange);
const initiativeRange = ref(props.initiativeRange);
const steadyRange = ref(props.steadyRange);
const cautiousRange = ref(props.cautiousRange);

const patterns = ref<{ [key: string]: number[] }>({
    d: [],
    i: [],
    s: [],
    g: [],
});

// Breakpoints for responsive design of diagram lines
const breakpoints = {
    mobile: 768,
    tablet: 825,
    desktop: 1000,
    wide: 1100
} as const;

const scale = computed(() => {
    const width = globalThis.innerWidth;
    if (width <= breakpoints.mobile) return 0.9;
    if (width <= breakpoints.tablet) return 0.75;
    if (width <= breakpoints.desktop) return 0.8;
    if (width <= breakpoints.wide) return 0.9;
    return 1;
});

const topDottedLinePosition = computed(() => 0.37 * scale.value * 100 + "%");
const bottomDottedLinePosition = computed(() => 0.79 * scale.value * 100 + "%");
const blackLinePosition = computed(() => 0.58 * scale.value * 100 + "%");

/*
 * The adjectives for each dimension (dominant, initiative, steady, cautious)
 * There are 3 adjectives for each level (very high, high, low, very low)
 */
const adjectives: Ref<{ identifier: string; text: Text }[]> = ref([]);
const initials: Ref<Record<string, string>> = ref({});
const dominantAdjectives = ref<string[]>([]);
const initiativeAdjectives = ref<string[]>([]);
const steadyAdjectives = ref<string[]>([]);
const cautiousAdjectives = ref<string[]>([]);

async function getPatterns() {
    const patterns = calculatePatternsFrontend();

    return {
        d: patterns.filter((pattern) => [1, 12, 13, 14, 124].includes(pattern)),
        i: patterns.filter((pattern) => [2, 21, 23, 24, 123].includes(pattern)),
        s: patterns.filter((pattern) => [3, 31, 32, 34, 234].includes(pattern)),
        g: patterns.filter((pattern) => [4, 41, 42, 43, 134].includes(pattern)),
    };
}

/**
 * Calculate the patterns for the diagramm in the frontend
 */
function calculatePatternsFrontend() {
    return getPatternsForDiagramm(
        dominantRange.value,
        initiativeRange.value,
        steadyRange.value,
        cautiousRange.value,
    )
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => patternOrder.indexOf(a.toString()) - patternOrder.indexOf(b.toString()));
}

watch(
    [dominantRange, initiativeRange, steadyRange, cautiousRange],
    async () => {
        patterns.value = await getPatterns();
        emit("update:range-values", [
            dominantRange.value,
            initiativeRange.value,
            steadyRange.value,
            cautiousRange.value,
        ]);
    },
    { immediate: true },
);

function getTextForDimension(dimension: string) {
    const levelsOrder = ["veryHigh", "high", "low", "veryLow"];
    const levelMapping = {
        "very.high": "veryHigh",
        ".high": "high",
        ".very.low": "veryLow",
        ".low": "low",
    };

    return adjectives.value
        .filter((text) => text.identifier.startsWith(`in120.adjectives.${dimension}`))
        .map((text) => {
            const level = Object.keys(levelMapping).find((suffix) => text.identifier.endsWith(suffix)) ?? "unknown";
            return {
                level: levelMapping[level as keyof typeof levelMapping] ?? "unknown",
                text: text.text.text.split(/\r?\n|\r/).map((t) => t.trim()),
            };
        })
        .filter((item) => item.level !== "unknown")
        .sort((a, b) => levelsOrder.indexOf(a.level) - levelsOrder.indexOf(b.level))
        .flatMap((item) => item.text);
}

function getAdjectivesFromProfileTexts(profileTexts: Identifier[]) {
    return profileTexts
        .filter((identifier) => identifier.identifier.startsWith("in120.adjectives"))
        .map((identifier) => {
            return {
                identifier: identifier.identifier,
                text: identifier.texts.find((t) => t.language === i18n.global.locale.value) ??
                    identifier.texts.find((t) => t.language === "en") ??
                    identifier.texts.find((t) => t.language === "de") ?? { id: 0, text: "", language: "de" },
            };
        });
}

function getDimensionInitialTexts(profileTexts: Identifier[]): Record<string, string> {
    return profileTexts
        .filter((identifier) => identifier.identifier.startsWith("in100.disc.itemtype"))
        .map((identifier) => {
            const text = identifier.texts.find((t) => t.language === i18n.global.locale.value) ||
                identifier.texts.find((t) => t.language === "en") ||
                identifier.texts.find((t) => t.language === "de") || { id: 0, text: "", language: "de" };

            return {
                identifier: identifier.identifier,
                text: text.text,
            };
        })
        .reduce<Record<string, string>>((map, item) => {
            map[item.identifier] = item.text;
            return map;
        }, {});
}

onMounted(async () => {
    const profileTexts = await LanguageManagementAPI.fetchTexts("profile-in120");
    adjectives.value = getAdjectivesFromProfileTexts(profileTexts);
    initials.value = getDimensionInitialTexts(profileTexts);
    dominantAdjectives.value = getTextForDimension("d");
    initiativeAdjectives.value = getTextForDimension("i");
    steadyAdjectives.value = getTextForDimension("s");
    cautiousAdjectives.value = getTextForDimension("g");
});
</script>

<template>
    <div class="benchmark-container" :style="{ '--line-top': topDottedLinePosition, '--line-bottom': bottomDottedLinePosition, '--black-line': blackLinePosition }">
        <div class="justify-center items-center">
            <div class="card diagram-container">
                <div class="dotted-line" :style="{ top: 'var(--line-top)' }"></div>
                <div class="black-line" :style="{ top: 'var(--black-line)'}"></div>
                <div class="dotted-line" :style="{ top: 'var(--line-bottom)'}"></div>
                <div class="diagram-front"></div>
                <div class="sliders-block">
                    <DimensionSlider
                        :range="dominantRange"
                        :title="initials['in100.disc.itemtype.d']"
                        :step="1"
                        :adjectives="dominantAdjectives"
                        slider-class="w-12 dominant-slider"
                        :disabled="!props.editable"
                        @update:range-value="dominantRange = $event"
                    />
                    <DimensionSlider
                        :range="initiativeRange"
                        :title="initials['in100.disc.itemtype.i']"
                        :step="1"
                        :adjectives="initiativeAdjectives"
                        slider-class="w-12 initiative-slider"
                        :disabled="!props.editable"
                        @update:range-value="initiativeRange = $event"
                    />
                    <DimensionSlider
                        :range="steadyRange"
                        :title="initials['in100.disc.itemtype.s']"
                        :step="1"
                        :adjectives="steadyAdjectives"
                        slider-class="w-12 steady-slider"
                        :disabled="!props.editable"
                        @update:range-value="steadyRange = $event"
                    />
                    <DimensionSlider
                        :range="cautiousRange"
                        :title="initials['in100.disc.itemtype.g']"
                        :step="1"
                        :adjectives="cautiousAdjectives"
                        slider-class="w-12 cautious-slider"
                        :disabled="!props.editable"
                        @update:range-value="cautiousRange = $event"
                    />
                </div>
                <div class="diagram-back"></div>
            </div>
        </div>
        <!-- Patterns -->
        <div class="patterns">
            <div class="flex-1 mb-2" v-for="dimension in ['D', 'I', 'S', 'G']" :key="dimension">
                <Chip
                    class="rounded py-0 pl-0 pr-4 mr-2"
                    v-for="pattern in patterns[dimension.toLowerCase()]"
                    :id="`pattern-${pattern}`"
                    :key="pattern"
                    :pt="{
                        root: {
                            style: {
                                background: 'transparent',
                                border: 'none'
                            }
                        }
                    }"
                >
                    <span
                        :class="dimension.toLowerCase() + ' rounded-full w-8 h-8 flex items-center justify-center'"
                    >
                        {{ initials[`in100.disc.itemtype.${dimension.toLowerCase()}`] }}
                    </span>
                    <span class="ml-2 font-medium">{{ pattern }}</span>
                </Chip>
            </div>
        </div>
    </div>
</template>

<style scoped>
.d {
    background-color: #ff103a;
    color: #fff;
}
.i {
    background-color: #ffc400;
    color: #fff;
}
.s {
    background-color: #00875d;
    color: #fff;
}
.g {
    background-color: #0066c1;
    color: #fff;
}

.patterns {
    margin-left: 130px;
    margin-right: 150px;
    display: flex;
    flex-wrap: wrap;
}

.benchmark-container {
    display: flex;
    flex-direction: column;
    background-color: #fafafa;
    padding: 0;
    max-width: 1500px;
    min-width: 60vw;
    margin: auto;
}

.diagram-container {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: 20px;
    background-color: #fafafa;
    padding: 0;
    position: relative;
}

.diagram-front {
    width: 50px;
    height: 418px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-image: url("images/in120-front.svg");
    background-size: cover;
}

.sliders-block {
    display: flex;
    justify-content: space-between;
    margin: auto;
    width: 80%;
}

@media screen and (min-width: 768px) and (max-width: 1100px) {
    .diagram-back,
    .diagram-front,
    .patterns,
    .sliders-block {
        transform: scale(0.9);
        transform-origin: left top;
    }
    .sliders-block {
        margin-left: 0;
        margin-right: 0;
    }
    .patterns {
        margin-left: auto;
        margin-right: auto;
        transform-origin: center top;
    }
}

@media screen and (min-width: 768px) and (max-width: 1000px) {
    .diagram-back,
    .diagram-front,
    .patterns,
    .sliders-block {
        transform: scale(0.8);
        transform-origin: left top;
    }
    .sliders-block {
        margin-left: 0;
        margin-right: 0;
    }
    .patterns {
        margin-left: auto;
        margin-right: auto;
        transform-origin: center top;
    }
    .benchmark-container {
        max-width: 100%;
        margin-left: 1em;
        margin-right: 1em;
    }
    .diagram-container {
        margin: 0;
    }
}

@media screen and (min-width: 768px) and (max-width: 825px) {
    .diagram-back,
    .diagram-front,
    .patterns,
    .sliders-block {
        transform: scale(0.75);
        transform-origin: left top;
    }
    .sliders-block {
        margin-left: 0;
        margin-right: 0;
    }
    .patterns {
        margin-left: auto;
        margin-right: auto;
        transform-origin: center top;
    }
    .benchmark-container {
        max-width: 100%;
    }
    .diagram-container {
        margin: 0;
    }
}

.diagram-back {
    width: 50px;
    height: 418px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-image: url("images/in120-back.svg");
    background-size: cover;
}

/* Black line */
.black-line {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background-color: black;
}

/* Dotted lines */
.dotted-line {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    border-top: 1px dashed black;
}

.rounded {
    border-radius: 16px !important;
}

/* Phones: one full-width block per dimension instead of the shrunken desktop diagram. The profile
   graphic and its reference lines have no room here; the level separators drawn by DimensionSlider
   take over that job. */
@media screen and (max-width: 767px) {
    .benchmark-container {
        min-width: 0;
        max-width: 100%;
        margin: 0;
    }
    .diagram-container {
        display: block;
        margin: 0;
    }
    .diagram-front,
    .diagram-back,
    .black-line,
    .dotted-line {
        display: none;
    }
    .sliders-block {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        width: 100%;
        margin: 0;
        padding: 1rem 0.75rem 0;
        transform: none;
    }
    .patterns {
        flex-direction: column;
        margin: 1rem 0.75rem 0;
        transform: none;
    }
    .patterns > div {
        display: flex;
        flex-wrap: wrap;
    }
}
</style>
